// src/pages/Register.js

import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Link, useNavigate } from 'react-router-dom'
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Grid,
  Paper,
  Avatar,
  Checkbox,
  FormControlLabel,
  CircularProgress
} from '@mui/material'
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

export default function Register() {
  const { t } = useTranslation() // i18n Hook

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [verificationSent, setVerificationSent] = useState(false)
  const { register } = useAuth()
  const navigate = useNavigate()

  // Checkbox states & popups
  const [checkedKVKK, setCheckedKVKK] = useState(false)
  const [checkedUyelikKosullari, setCheckedUyelikKosullari] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!checkedKVKK || !checkedUyelikKosullari) {
      return setError(t('registerPage.checkboxError'))
    }

    if (password !== confirmPassword) {
      return setError(t('registerPage.passwordMismatchError'))
    }

    try {
      setError('')
      setLoading(true)
      await register(email, password)
      setVerificationSent(true)
    } catch (err) {
      setError(t('registerPage.genericError'))
    } finally {
      setLoading(false)
    }
  }

  if (verificationSent) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #fdfbfb 0%, #f3f4f6 40%, #e0f2fe 100%)',
          px: 2
        }}
      >
        <Container component="main" maxWidth="xs" disableGutters>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 4,
              boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
              backgroundColor: 'rgba(255,255,255,0.96)',
              border: '1px solid rgba(148,163,184,0.25)',
              backdropFilter: 'blur(10px)',
              textAlign: 'center'
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              {t('registerPage.successTitle')}
            </Typography>
            <Typography gutterBottom color="text.secondary">
              {t('registerPage.successDesc')}
            </Typography>
            <Box mt={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/login')}
                sx={{
                  px: 4,
                  py: 1.3,
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 14px 30px rgba(37,99,235,0.4)'
                }}
              >
                {t('registerPage.goToLoginPageButton')}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #fdfbfb 0%, #f3f4f6 40%, #e0f2fe 100%)',
        px: 2
      }}
    >
      <Container component="main" maxWidth="xs" disableGutters>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
            backgroundColor: 'rgba(255,255,255,0.96)',
            border: '1px solid rgba(148,163,184,0.25)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <Avatar
              sx={{
                mb: 0.5,
                width: 56,
                height: 56,
                bgcolor: 'secondary.main',
                boxShadow: '0 10px 25px rgba(244,114,182,0.45)'
              }}
            >
              <PersonAddOutlinedIcon />
            </Avatar>
            <Box sx={{ textAlign: 'center' }}>
              <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
                {t('registerPage.title')}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {t('registerPage.subtitle') || ''}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mt: 1.5, borderRadius: 3 }}>
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ mt: 2, width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    required
                    fullWidth
                    type="email"
                    label={t('registerPage.emailLabel')}
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: 'rgba(248,250,252,0.9)'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    required
                    fullWidth
                    type="password"
                    label={t('registerPage.passwordLabel')}
                    variant="outlined"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: 'rgba(248,250,252,0.9)'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    required
                    fullWidth
                    type="password"
                    label={t('registerPage.confirmPasswordLabel')}
                    variant="outlined"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: 'rgba(248,250,252,0.9)'
                      }
                    }}
                  />
                </Grid>

                {/* 1. CheckBox - Kişisel Veriler Aydınlatma Metni */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={checkedKVKK}
                        onChange={(e) => setCheckedKVKK(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="body2" component="span">
                          {t('registerPage.personalDataChecked')}{' '}
                        </Typography>
                        <a
                          href="https://www.evoosurvey.com/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: '0.85rem' }}
                        >
                          {t('registerPage.privacyPolicyLink')}
                        </a>
                      </Box>
                    }
                    sx={{ alignItems: 'center' }}
                  />
                </Grid>

                {/* 3. CheckBox - Üyelik Koşulları */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={checkedUyelikKosullari}
                        onChange={(e) => setCheckedUyelikKosullari(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="body2" component="span">
                          {t('registerPage.membershipTermsChecked')}{' '}
                        </Typography>
                        <a
                          href="https://www.evoosurvey.com/terms-conditions"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: '0.85rem' }}
                        >
                          {t('registerPage.termsConditionsLink')}
                        </a>
                      </Box>
                    }
                    sx={{ alignItems: 'center' }}
                  />
                </Grid>
              </Grid>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{
                  mt: 1.5,
                  mb: 0.5,
                  py: 1.3,
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  boxShadow: '0 14px 30px rgba(37,99,235,0.4)'
                }}
              >
                {loading
                  ? <CircularProgress size={24} color="inherit" />
                  : t('registerPage.registerButton')
                }
              </Button>

              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('registerPage.alreadyHaveAccountPrefix') || t('registerPage.alreadyHaveAccount')}
                  {' '}
                  <Link to="/login" style={{ textDecoration: 'none' }}>
                    <Typography
                      component="span"
                      variant="body2"
                      color="primary.main"
                      sx={{ fontWeight: 600 }}
                    >
                      {t('registerPage.alreadyHaveAccountLink') || t('registerPage.alreadyHaveAccount')}
                    </Typography>
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}